import { resolveBrandColors } from "@/shared/lib/brand/colors";
/**
 * 品牌资产卡片 —— Logo / 二维码 / 品牌色。
 */
import { ChevronRight, QrCode } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";

import { Button } from "@/shared/ui/button";
import { Switch } from "@/shared/ui/switch";
import { AssetSwitcher } from "../../../ui/components/free-create/AssetSwitcher";
import { AnchorGrid9 } from "./AnchorGrid9";
import type { AnchorKey9 } from "../../../api/posterService";
import { UrlQrPreview } from "@/shared/ui/UrlQrPreview";
import { PosterCard, PosterCardHeader, PosterSectionTitle } from "@/shared/ui/PosterCard";
import { useBrandAssets } from "@/entities/brand-asset";
import type { LogoEntry, QrEntry } from "@/entities/brand-asset";

type BrandData = NonNullable<ReturnType<typeof useBrandAssets>["data"]>;

interface Props {
  brand: BrandData | undefined;
  logoId: string | null;
  setLogoId: (v: string | null) => void;
  qrId: string | null;
  setQrId: (v: string | null) => void;
  useBrandColors: boolean;
  setUseBrandColors: (v: boolean) => void;
  /** 任务 14:九宫格锚点。null = 自动(按画面留白挑角)。 */
  logoAnchor?: AnchorKey9 | null;
  setLogoAnchor?: (v: AnchorKey9 | null) => void;
  qrAnchor?: AnchorKey9 | null;
  setQrAnchor?: (v: AnchorKey9 | null) => void;
  printContact: boolean;
  setPrintContact: (v: boolean) => void;
  /**
   * 2026-07-30 —— 窄容器形态(大框那一行的弹层里用)。
   *
   * ★病根:原来这里是 `grid-cols-2 sm:grid-cols-4`,而 Tailwind 的 `sm:` 是**视口**断点、
   *   不是**容器**断点。把这个面板塞进 360px 弹层之后,它仍然按「视口 ≥640px」排 4 列 ——
   *   每列约 78px,中文只能竖着排(用户原话:「展开字都显示的是竖的了」)。
   * ★所以不是"再加一个更小的断点"能解决的:容器宽度与视口宽度在弹层里根本不相关。
   *   dense 由**调用方**声明"我很窄",不去猜。
   */
  dense?: boolean;
}

export function BrandPanel({
  brand,
  logoId,
  setLogoId,
  qrId,
  setQrId,
  useBrandColors,
  setUseBrandColors,
  logoAnchor = null,
  setLogoAnchor,
  qrAnchor = null,
  setQrAnchor,
  printContact,
  setPrintContact,
  dense = false,
}: Props) {
  // 品牌资料里一个都没填 → 开关没有意义,禁用并说清楚去哪儿填。
  // ★不是"隐藏":藏起来的话,用户永远不会知道海报能印联系方式(这正是它当了两个月
  //   死通道也没人报的原因之一 —— 界面从没承认过它的存在)。
  const contactLines = [brand?.phone, brand?.email].filter(Boolean) as string[];
  const hasContact = contactLines.length > 0;
  const { t } = useTranslation("poster");
  const navigate = useNavigate();
  const inner = (
    <>
      <div className={dense ? "flex flex-col gap-2.5" : "grid grid-cols-2 gap-3 sm:grid-cols-4"}>
        <AssetSwitcher<LogoEntry>
          label={t("freeCreate.logo")}
          items={brand?.logos ?? []}
          selectedId={logoId}
          onSelect={setLogoId}
          emptyHint={t("freeCreate.logoEmpty")}
          renderItem={(l) => (
            <div className="flex min-w-0 items-center gap-2">
              <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded border border-primary/40 bg-primary/10 font-mono text-[13px] font-bold text-primary">
                {l.name.slice(0, 1).toUpperCase()}
              </div>
              <span className="truncate text-[13px]">{l.name}</span>
            </div>
          )}
        />
        {/* ★只有真挂了 logo 才给位置控件 —— 给一个不存在的元素定位置是无意义的脏数据 */}
        {logoId && setLogoAnchor && (
          <AnchorGrid9
            label="Logo 位置"
            value={logoAnchor}
            onChange={setLogoAnchor}
            autoHint="按画面留白自动挑最空的角"
          />
        )}

        <AssetSwitcher<QrEntry>
          label={t("freeCreate.qrcode")}
          items={brand?.qrs ?? []}
          selectedId={qrId}
          onSelect={setQrId}
          emptyHint={t("freeCreate.qrEmpty")}
          renderItem={(q) => (
            <div className="flex min-w-0 items-center gap-2">
              <div className="flex h-8 w-8 shrink-0 items-center justify-center overflow-hidden rounded border bg-white">
                {q.mode === "URL" && q.url ? (
                  <UrlQrPreview url={q.url} size={32} />
                ) : (
                  <QrCode className="h-5 w-5 text-black" />
                )}
              </div>
              <span className="truncate text-[13px]">{q.name}</span>
            </div>
          )}
        />
        {qrId && setQrAnchor && (
          <AnchorGrid9
            label="二维码位置"
            value={qrAnchor}
            onChange={setQrAnchor}
            autoHint="按画面留白自动挑最空的角(二维码优先占最静的那个)"
          />
        )}

        {/* 品牌色预览块 + V11.6 toggle 本次启用 */}
        <div className="rounded-lg border border-border/60 bg-card/40 p-2.5">
          <div className="mb-1.5 flex items-center justify-between">
            <span className="text-2xs uppercase tracking-wider text-muted-foreground">
              {t("freeCreate.brandColor")}
            </span>
            <Switch checked={useBrandColors} onCheckedChange={setUseBrandColors} />
          </div>
          <div className="flex items-center gap-1.5 p-1">
            {brand
              ? Object.values(resolveBrandColors(brand?.colors)).map((c) => (
                  <span
                    key={c}
                    className="h-5 w-5 rounded-full border border-white/20"
                    style={{ backgroundColor: c }}
                    title={c}
                  />
                ))
              : null}
          </div>
        </div>
        {/* 2026-07-30 — 联系方式上图开关(此前 phone/email 是条死通道) */}
        <div className="rounded-lg border border-border/60 bg-card/40 p-2.5">
          <div className="mb-1.5 flex items-center justify-between">
            <span className="text-2xs uppercase tracking-wider text-muted-foreground">
              {t("freeCreate.printContact")}
            </span>
            <Switch
              checked={printContact && hasContact}
              onCheckedChange={setPrintContact}
              disabled={!hasContact}
              aria-label={t("freeCreate.printContact")}
            />
          </div>
          {hasContact ? (
            <div className="space-y-0.5 p-1">
              {contactLines.map((c) => (
                <div key={c} className="truncate font-mono text-[12.5px] text-foreground" title={c}>
                  {c}
                </div>
              ))}
            </div>
          ) : (
            <div className="p-1 text-[12.5px] text-muted-foreground">
              {t("freeCreate.printContactEmpty")}
            </div>
          )}
        </div>
      </div>
    </>
  );

  if (dense) {
    return (
      <div className="space-y-3" data-brand-panel="dense">
        <div className="flex items-center justify-between">
          <span className="text-[13px] font-semibold">{t("freeCreate.brandTitle")}</span>
          <Button
            size="sm"
            variant="ghost"
            className="h-7 gap-1 text-[12.5px] cursor-pointer"
            onClick={() => navigate("/tenant-admin/brand-assets")}
          >
            {t("actions.manage")}
            <ChevronRight className="h-3 w-3" />
          </Button>
        </div>
        {inner}
      </div>
    );
  }

  return (
    <PosterCard>
      <PosterCardHeader>
        <div className="flex items-center justify-between">
          <PosterSectionTitle title={t("freeCreate.brandTitle")} hint={t("freeCreate.brandHint")} />
          <Button
            size="sm"
            variant="ghost"
            className="h-7 gap-1 text-[13px] cursor-pointer"
            onClick={() => navigate("/tenant-admin/brand-assets")}
          >
            {t("actions.manage")}
            <ChevronRight className="h-3 w-3" />
          </Button>
        </div>
      </PosterCardHeader>
      <div className="px-5 pb-5">{inner}</div>
    </PosterCard>
  );
}
